import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { KeycloakAuthGuard, KeycloakService } from 'keycloak-angular';
import { KeycloakInitService } from '../foundry-auth-services/keycloak-init.service';
import { RoleAccessService } from '@shared-lib/foundry-shared/role-access.service';
import { ModuleFlagsService } from '@shared-lib/foundry-shared/module-flags.service';
import { PublishedScreenService } from '../../screen-renderer/published/published-screen.service';

@Injectable({
  providedIn: 'root'
})

export class AuthGuard extends KeycloakAuthGuard {
  constructor(
    protected override readonly router: Router,
    protected readonly keycloak: KeycloakService,
    private readonly roleAccess: RoleAccessService,
    private readonly moduleFlags: ModuleFlagsService,
    private readonly keycloakInit: KeycloakInitService,
    private readonly publishedScreens: PublishedScreenService,
  ) {
    super(router, keycloak)
  }
  async isAccessAllowed(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    // 1) Authentication — redirect to Keycloak login if not signed in.
    if (!this.authenticated) {
      await this.keycloak.login({
        redirectUri: window.location.origin + state.url,
      });
      return false;
    }

    // 2) Authorization — resolve the user's role. For now roles are assigned by
    //    email in user-role-map.json; if the email isn't listed we fall back to
    //    the roles from the Keycloak token (`this.roles`). Then check the
    //    resolved roles against the route access map.
    await Promise.all([
      this.roleAccess.load(),
      this.moduleFlags.load(),
      this.keycloakInit.loadAuthenticatedUserPermissions(),
    ]);
    const email = String((this.keycloak.getKeycloakInstance()?.tokenParsed as any)?.email || '');
    const roles = this.roleAccess.resolveRoles(email, this.roles);

    this.roleAccess.setCurrentRoles(roles);

    let accessKey = String(route.data?.['accessKey'] || route.routeConfig?.path || '');
    if (route.routeConfig?.path === ':screenSlug') {
      try {
        const screen = await this.publishedScreens.load(route.paramMap.get('screenSlug') ?? '');
        accessKey = screen.accessKey;
      } catch { return this.router.parseUrl('/home-main'); }
    }
    const moduleAvailable = this.moduleFlags.isVisible(accessKey) &&
      this.moduleFlags.isActive(accessKey);
    if (moduleAvailable && this.roleAccess.canAccess(accessKey, roles)) {
      return true;
    }

    const fallback = [
      this.roleAccess.fallbackRoute,
      ...this.moduleFlags.getAvailableScreenIds(),
    ].find((screenId) =>
      screenId !== accessKey &&
      this.moduleFlags.isVisible(screenId) &&
      this.moduleFlags.isActive(screenId) &&
      this.roleAccess.canAccess(screenId, roles)
    );

    return fallback ? this.router.parseUrl('/' + fallback) : false;
  }
}
